/**
 * Mobile Detection Hook
 *
 * Provides comprehensive mobile device detection and responsive utilities.
 * Uses both CSS media queries and user agent detection for accurate results.
 */

import { useMediaQuery, useTheme } from '@chakra-ui/react';
import { useCallback, useEffect, useState } from 'react';
import { MOBILE_VIEWPORT_QUERY } from '../../utils/bowerClient';

interface MobileDetection {
  // Screen size detection
  isMobile: boolean;
  isTablet: boolean;
  isDesktop: boolean;

  // Device capabilities
  isTouchDevice: boolean;
  hasCamera: boolean;
  hasMicrophone: boolean;

  // Platform detection
  isIOS: boolean;
  isAndroid: boolean;

  // App-like experience detection
  isPWA: boolean;
  isStandalone: boolean;

  // Quick capture readiness
  canQuickCapture: boolean;

  // True after the initial async device enumeration completes.
  // Use this to avoid flashing fallback UI (e.g. "Grant Access") while
  // hasCamera/hasMicrophone are still being determined.
  isMediaDetected: boolean;

  // Permission request — triggers getUserMedia prompt, then re-enumerates devices
  requestMediaAccess: () => Promise<{ camera: boolean; microphone: boolean }>;
}

export const useMobileDetection = (): MobileDetection => {
  // One subscription for every media query this hook reads. It used three
  // useBreakpointValue calls, each subscribed to all six theme breakpoints and
  // updating on its own, so every consumer (Layout, ProtectedRoute, every item
  // page through useWorkspaceTopBar) re-rendered up to six times per breakpoint
  // a window resize crossed, including breakpoints no answer here depends on.
  // `ssr: false` reads the real values on the first render.
  const { breakpoints } = useTheme();
  const [isMobileBreakpoint, isLgUp, isTouch] = useMediaQuery(
    [
      MOBILE_VIEWPORT_QUERY,
      `(min-width: ${breakpoints.lg})`,
      '(hover: none) and (pointer: coarse)',
    ],
    { ssr: false },
  );
  const isTabletBreakpoint = !isMobileBreakpoint && !isLgUp;
  const isDesktopBreakpoint = isLgUp;

  // State for device capabilities
  // Initialize platform detection synchronously from user agent to avoid
  // a flash of desktop layout while useEffect hasn't run yet.
  const [deviceCapabilities, setDeviceCapabilities] = useState(() => {
    const userAgent = navigator.userAgent.toLowerCase();
    const isPWA = window.matchMedia('(display-mode: standalone)').matches;
    return {
      hasCamera: false,
      hasMicrophone: false,
      isIOS: /iphone|ipad|ipod/.test(userAgent),
      isAndroid: /android/.test(userAgent),
      isPWA,
      isStandalone: (navigator as any).standalone === true || isPWA,
    };
  });

  // Tracks whether the initial async media device enumeration has completed.
  const [isMediaDetected, setIsMediaDetected] = useState(false);

  const enumerateMedia = useCallback(async () => {
    let hasCamera = false;
    let hasMicrophone = false;

    try {
      if (navigator.mediaDevices?.enumerateDevices) {
        const devices = await navigator.mediaDevices.enumerateDevices();
        hasCamera = devices.some((device) => device.kind === 'videoinput');
        hasMicrophone = devices.some((device) => device.kind === 'audioinput');
      }
    } catch (error) {
      console.warn('Could not enumerate media devices:', error);
      // Fallback: assume mobile devices have camera/mic
      hasCamera = isMobileBreakpoint;
      hasMicrophone = isMobileBreakpoint;
    }

    return { hasCamera, hasMicrophone };
  }, [isMobileBreakpoint]);

  useEffect(() => {
    const checkDeviceCapabilities = async () => {
      // Check user agent for platform detection
      const userAgent = navigator.userAgent.toLowerCase();
      const isIOS = /iphone|ipad|ipod/.test(userAgent);
      const isAndroid = /android/.test(userAgent);

      // Check for PWA/standalone mode
      const isPWA = window.matchMedia('(display-mode: standalone)').matches;
      const isStandalone = (window.navigator as any).standalone === true || isPWA;

      const { hasCamera, hasMicrophone } = await enumerateMedia();

      setDeviceCapabilities({
        hasCamera,
        hasMicrophone,
        isIOS,
        isAndroid,
        isPWA,
        isStandalone,
      });
      setIsMediaDetected(true);
    };

    checkDeviceCapabilities();
  }, [isMobileBreakpoint, enumerateMedia]);

  // Triggers getUserMedia to prompt the browser permission dialog,
  // then re-enumerates devices to update hasCamera/hasMicrophone.
  // On iOS Safari, enumerateDevices() often returns empty until
  // a getUserMedia() call has been granted at least once.
  const requestMediaAccess = useCallback(async () => {
    let camera = false;
    let microphone = false;

    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true, video: true });
      stream.getTracks().forEach((track) => track.stop());
      camera = true;
      microphone = true;
    } catch {
      // Partial grant or denial — try each separately
      try {
        const audioStream = await navigator.mediaDevices.getUserMedia({ audio: true });
        audioStream.getTracks().forEach((track) => track.stop());
        microphone = true;
      } catch {
        // Microphone denied or unavailable
      }
      try {
        const videoStream = await navigator.mediaDevices.getUserMedia({ video: true });
        videoStream.getTracks().forEach((track) => track.stop());
        camera = true;
      } catch {
        // Camera denied or unavailable
      }
    }

    // Re-enumerate now that permissions may have changed
    const { hasCamera, hasMicrophone } = await enumerateMedia();

    setDeviceCapabilities((prev) => ({
      ...prev,
      hasCamera: hasCamera || camera,
      hasMicrophone: hasMicrophone || microphone,
    }));

    return { camera: hasCamera || camera, microphone: hasMicrophone || microphone };
  }, [enumerateMedia]);

  // Determine if this is truly a mobile device (not just small screen)
  const isMobile =
    isMobileBreakpoint && (isTouch || deviceCapabilities.isIOS || deviceCapabilities.isAndroid);

  // Quick capture readiness: mobile device with camera and microphone
  const canQuickCapture =
    isMobile && deviceCapabilities.hasCamera && deviceCapabilities.hasMicrophone;

  return {
    // Screen size detection
    isMobile,
    isTablet: isTabletBreakpoint && !isMobile,
    isDesktop: isDesktopBreakpoint,

    // Device capabilities
    isTouchDevice: isTouch,
    hasCamera: deviceCapabilities.hasCamera,
    hasMicrophone: deviceCapabilities.hasMicrophone,

    // Platform detection
    isIOS: deviceCapabilities.isIOS,
    isAndroid: deviceCapabilities.isAndroid,

    // App-like experience
    isPWA: deviceCapabilities.isPWA,
    isStandalone: deviceCapabilities.isStandalone,

    // Quick capture readiness
    canQuickCapture,

    // Media detection status
    isMediaDetected,

    // Permission request
    requestMediaAccess,
  };
};
